import { useRef } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Messages } from 'primereact/messages';

import { FormContainer, FormInputText, FormDropdown, FormGoogleAddressLookup, FormMultiSelect } from '../../components/form';
import { OrgUnitUserDto } from '../../models/user-account';

import { AddressComponents } from '../../components/form/FormGoogleAddressLookup';

const timezoneOptions = [
  { label: 'Eastern Time (ET)', value: 'America/New_York' },
  { label: 'Central Time (CT)', value: 'America/Chicago' },
  { label: 'Mountain Time (MT)', value: 'America/Denver' },
  { label: 'Pacific Time (PT)', value: 'America/Los_Angeles' },
  { label: 'Alaska Time (AKT)', value: 'America/Anchorage' },
  { label: 'Hawaii Time (HT)', value: 'America/Honolulu' },
];

const roleOptions = [
  { label: 'User', value: 'User' },
  { label: 'Admin', value: 'Admin' },
  { label: 'SuperAdmin', value: 'SuperAdmin' },
];

// Props interface for the component
interface EditOrgUnitUserProps {
  onSave: (_user: OrgUnitUserDto) => void;
  onHide: () => void;
  user?: OrgUnitUserDto;
}

export default function EditOrgUnitUser(props: EditOrgUnitUserProps) {
  const { onSave, onHide, user } = props;

  // Reference to the Messages component for showing errors
  const errors = useRef<Messages>(null);

  // Form validation schema
  const validationSchema = yup.object({
    userAccount: yup
      .object({
        email: yup.string().email('Invalid email format').required('Email is required').trim(),
        firstName: yup.string().required('First name is required').trim(),
        lastName: yup.string().required('Last name is required').trim(),
        timezone: yup.string().nullable(),
        nmlsId: yup.string().nullable(),
        roles: yup.array().min(1, 'At least one role is required').required('Roles are required'),
      })
      .required(),
    losRole: yup.mixed<'LoanOriginator' | 'Processor' | 'Other'>().nullable(),
  });

  const {
    control,
    handleSubmit,
    formState: { errors: formErrors },
    setValue,
  } = useForm<OrgUnitUserDto>({
    resolver: yupResolver<any>(validationSchema),
    defaultValues: user
      ? {
          ...user,
          userAccount: {
            ...user.userAccount,
            roles: user.userAccount.roles || [],
          },
          losRole: user.losRole ?? null,
        }
      : {
          userAccount: {
            email: '',
            firstName: '',
            lastName: '',
            timezone: 'America/New_York',
            nmlsId: '',
            roles: [],
          },
          losRole: null,
        },
  });

  // Show errors in the UI if validation fails
  const showFormErrors = () => {
    if (Object.keys(formErrors).length > 0) {
      // Create a formatted error message
      const errorMessages = processErrors(formErrors);

      // Display the error messages
      errors.current?.show({
        severity: 'error',
        summary: 'Form Validation Error',
        detail: errorMessages.join('\n'),
        sticky: true,
      });
      return true;
    }
    return false;
  };

  // Process nested errors
  const processErrors = (errors: any, path = '') => {
    const messages: string[] = [];

    for (const key in errors) {
      const errorObj = errors[key];
      const fullPath = path ? `${path}.${key}` : key;

      if (errorObj.message) {
        messages.push(errorObj.message);
      }

      if (errorObj.type === 'object' && errorObj.message) {
        messages.push(...processErrors(errorObj, fullPath));
      }
    }

    return messages;
  };

  // Handle form submission
  const onSubmit = (data: OrgUnitUserDto) => {
    errors.current?.clear();

    if (showFormErrors()) {
      return;
    }

    const orgUserToSave = {
      ...data,
      userAccount: {
        ...data.userAccount,
        primaryOrgUnitId: data.orgUnitId,
      },
    };

    // Call the onSave callback with the mapped form data
    onSave(orgUserToSave);
  };

  return (
    <div className="edit-user-form">
      <Messages ref={errors} />

      <FormContainer onSubmit={handleSubmit(onSubmit)} loading={false} onCancel={onHide} errors={errors}>
        <div className="form-section">
          <div className="formgrid grid">
            <div className="field col-12">
              <div
                className="my-2 p-2 bg-gray-100 border-round mx-0"
                style={{ marginLeft: '-1rem', marginRight: '-1rem', borderRadius: 0, paddingLeft: '1rem' }}
              >
                <div className="flex align-items-center">
                  <i className="pi pi-user mr-2"></i>
                  <span className="font-bold text-lg">User Information</span>
                </div>
              </div>
            </div>

            <div className="field col-12 md:col-6">
              <FormInputText
                control={control}
                errors={formErrors}
                field="userAccount.firstName"
                label="First Name"
                placeholder="Enter first name"
              />
            </div>

            <div className="field col-12 md:col-6">
              <FormInputText
                control={control}
                errors={formErrors}
                field="userAccount.lastName"
                label="Last Name"
                placeholder="Enter last name"
              />
            </div>

            <div className="field col-12">
              <FormInputText
                control={control}
                errors={formErrors}
                field="userAccount.email"
                label="Email"
                placeholder="Enter email address"
              />
            </div>

            <div className="field col-12 md:col-6">
              <FormInputText
                control={control}
                errors={formErrors}
                field="userAccount.nmlsId"
                label="NMLS ID"
                placeholder="Enter NMLS ID"
              />
            </div>

            <div className="field col-12 md:col-6">
              <FormDropdown
                control={control}
                errors={formErrors}
                field="userAccount.timezone"
                label="Timezone"
                options={timezoneOptions}
                placeholder="Select timezone"
              />
            </div>

            <div className="field col-12 md:col-6">
              <FormMultiSelect
                control={control}
                errors={formErrors}
                field="userAccount.roles"
                label="Roles"
                options={roleOptions}
              />
            </div>
            <div className="field col-12 md:col-6">
              <FormDropdown
                control={control}
                errors={formErrors}
                field="losRole"
                label="LOS Role"
                options={[
                  { label: 'Loan Originator', value: 'LoanOriginator' },
                  { label: 'Processor', value: 'Processor' },
                  { label: 'Other', value: 'Other' },
                ]}
                placeholder="Select LOS Role"
                showClear={true}
              />
            </div>
          </div>
        </div>
      </FormContainer>
    </div>
  );
}
